<template>
    <header v-if="!isPointResourcePage" :class="['production-progress__header', { 'remove-fixed': !isFixed }]">
        <div class="production-progress__back-labels-container">
            <div class="production-progress__back-button">
                <button @click="backEvent">
                    <ArrowLeftIcon />
                </button>
                <span>{{ backLabel }}</span>
                <button v-if="hasRNC" class="rnc-checklist-button" @click="onOpenRNCChecklist">
                    {{ $t("ProductionProgress.RNCChecklist") }}
                </button>
            </div>

            <div :class="[
                'production-progress__labels-container',
                !isFixed ? '--partial-button-header' : ''
            ]">
                <div v-if="orders.length === 1" class="single-order-block">
                    <div :id="`${componentId}-appointment-header`"
                        class="production-progress__single-order text-ellipsis">
                        {{ orders[0].name }}
                    </div>
                    <b-tooltip custom-class="tooltip-appointment-header" :target="`${componentId}-appointment-header`"
                        triggers="hover" placement="bottom">
                        {{ orders[0].name }}
                    </b-tooltip>
                </div>
                <MultipleOrderList :orders="orders" :removeOrder="removeOrder" @removeAppointment="removeAppointment"
                    v-else />
                <div class="header-stats-wrapper">
                    <div v-if="showTotalTime && formattedTotalOpDuration" :class="['header-total-time-block --in-labels-container', !isFixed ? '--partial-button-header' : '']" :title="formattedTotalOpDurationExact">
                        <div class="header-total-time-title">{{ $t("ProductionProgress.TotalOpTime") || "Tempo total OP:" }}</div>
                        <div class="header-total-time-value">{{ formattedTotalOpDuration }}</div>
                    </div>
                    <div v-if="!isPointResourcePage" :class="['quantity-block', !isFixed ? '--partial-button-header' : '']">
                        <div class="quantity-title">{{ $t("QTYPROD") }}</div>
                        <div class="quantity-values-planned-produced">
                            <span class="produced">{{
                                `${orderObject.producedQuantity ? orderObject.producedQuantity : 0}`
                            }}</span>{{ ` / ${orderObject.plannedQuantity ? orderObject.plannedQuantity : 0}` }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="production-progress__status-container text-ellipsis"
            :style="statusStyle">
            <span>{{ status }}</span>
        </div>
    </header>
    <header v-else :class="['production-progress__header point-resource-header', { 'remove-fixed': !isFixed }]">
        <div class="production-progress__back-labels-container">
            <div class="production-progress__back-button">
                <button @click="backEvent">
                    <ArrowLeftIcon />
                </button>
            </div>

            <div class="production-progress__labels-container">{{ resourceName }}</div>
        </div>
        <div v-if="showTotalTime && formattedTotalOpDuration" class="header-total-time-block" :title="formattedTotalOpDurationExact">
            <div class="header-total-time-title">{{ $t("ProductionProgress.TotalOpTime") || "Tempo total OP:" }}</div>
            <div class="header-total-time-value">{{ formattedTotalOpDuration }}</div>
        </div>
        <slot name="actions" />
        <div :class="['production-progress__status-container text-ellipsis', status]"
            :style="statusStyle">
            <span>{{ status }}</span>
        </div>
    </header>
</template>

<script>
import MultipleOrderList from "./components/MultipleOrderList";
import ArrowLeftIcon from "@/assets/images/new-icons/arrow-left.svg";
import { BTooltip } from "bootstrap-vue";
import { formatDurationHms, isFinishedOperationStatus } from "@/views/pages/operator/utils";

export default {
    components: {
        ArrowLeftIcon,
        MultipleOrderList,
        BTooltip
    },
    props: {
        componentId: {
            type: String,
            default: ""
        },
        backLabel: {
            type: String,
            default: ""
        },
        backEvent: {
            type: Function,
            default: () => { }
        },
        orders: {
            type: Array,
            default: () => []
        },
        orderObject: {
            default: null
        },
        removeOrder: {
            type: Function,
            default: () => { }
        },
        status: {
            type: String,
            default: ""
        },
        color: {
            type: String,
            default: ""
        },
        resourceName: {
            type: String,
            default: ""
        },
        isFixed: {
            type: Boolean,
            default: false
        },
        isPointResourcePage: {
            type: Boolean,
            default: false
        },
        hasRNC: {
            type: Boolean,
            default: false
        },
        onOpenRNCChecklist: {
            type: Function,
            default: () => { }
        },
        showTotalTime: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            now: Date.now(),
            interval: null
        };
    },
    mounted() {
        this.interval = setInterval(() => {
            this.now = Date.now();
        }, 1000);
    },
    beforeDestroy() {
        if (this.interval) {
            clearInterval(this.interval);
            this.interval = null;
        }
    },
    computed: {
        totalOpElapsedSeconds() {
            if (!this.orderObject) return null;
            const statusName = this.status || this.orderObject?.lastInfo?.lastStatus || this.orderObject?.currentStatusName;
            const normStatus = (statusName || "").trim().toLowerCase();
            if (!normStatus || ["não iniciado", "not started"].includes(normStatus)) {
                return null;
            }
            const isFinished = isFinishedOperationStatus(statusName) ||
                ["operação finalizada", "operacao finalizada", "completed", "finalizado", "finalizada", "concluído", "concluido"].includes(normStatus);

            const startTimeStr = this.orderObject?.firstEventTime
                || this.orderObject?.lastInfo?.firstEventTime
                || this.orderObject?.startTime
                || this.orderObject?.setupStartedAt
                || this.orderObject?.executionStartedAt
                || (!isFinished ? this.orderObject?.statusStartedAt : null)
                || this.orderObject?.lastInfo?.lastStarted;
            if (!startTimeStr) return null;

            const startedAt = this.parseDateToMillis(startTimeStr);
            if (!Number.isFinite(startedAt)) return null;

            if (isFinished) {
                const finishedTimeStr = this.orderObject?.finishedAt
                    || this.orderObject?.lastInfo?.lastCompleted
                    || this.orderObject?.lastInfo?.lastCompletedByUser
                    || this.orderObject?.extrusionFinishedAt
                    || this.orderObject?.statusStartedAt
                    || this.orderObject?.endTime
                    || this.orderObject?.lastInfo?.lastEventTime;
                if (!finishedTimeStr) return null;
                const finishedAt = this.parseDateToMillis(finishedTimeStr);
                if (!Number.isFinite(finishedAt)) return null;
                return Math.floor(Math.max(0, finishedAt - startedAt) / 1000);
            }

            return Math.floor(Math.max(0, this.now - startedAt) / 1000);
        },
        formattedTotalOpDuration() {
            if (this.totalOpElapsedSeconds === null) return null;
            const totalSec = this.totalOpElapsedSeconds;
            const h = Math.floor(totalSec / 3600);
            const m = Math.floor((totalSec % 3600) / 60);
            return `${String(h).padStart(2, "0")} : ${String(m).padStart(2, "0")} h`;
        },
        formattedTotalOpDurationExact() {
            if (this.totalOpElapsedSeconds === null) return null;
            return formatDurationHms(this.totalOpElapsedSeconds, "seconds");
        },
        statusStyle() {
            const rawColor = (this.color || "#61b3fa").trim();
            let bg = `${rawColor}20`;
            if (rawColor.startsWith("#")) {
                let hex = rawColor.replace("#", "");
                if (hex.length === 3) hex = hex.split("").map(c => c + c).join("");
                if (hex.length >= 6) {
                    const r = parseInt(hex.substring(0, 2), 16);
                    const g = parseInt(hex.substring(2, 4), 16);
                    const b = parseInt(hex.substring(4, 6), 16);
                    if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
                        bg = `rgba(${r}, ${g}, ${b}, 0.15)`;
                    }
                }
            }
            return {
                backgroundColor: bg,
                color: rawColor
            };
        }
    },
    methods: {
        removeAppointment(appointmentId) {
            this.$emit("removeAppointment", appointmentId);
        },
        parseDateToMillis(val) {
            if (!val) return null;
            if (val instanceof Date) return val.getTime();
            if (typeof val === "number") return val;
            const strVal = String(val).trim();
            if (!strVal) return null;
            let str = strVal.replace(" ", "T");
            if (str.includes("T") && !str.endsWith("Z") && !/[+-]\d{2}:\d{2}$/.test(str)) {
                const parts = str.split("T");
                const dateParts = parts[0].split("-").map(Number);
                const timeParts = parts[1].split(".")[0].split(":").map(Number);
                if (dateParts.length === 3 && timeParts.length >= 2) {
                    return new Date(dateParts[0], dateParts[1] - 1, dateParts[2], timeParts[0], timeParts[1], timeParts[2] || 0).getTime();
                }
            }
            const parsed = Date.parse(str);
            return Number.isFinite(parsed) ? parsed : null;
        }
    }
};
</script>

<style lang="scss">
.tooltip-appointment-header {
    z-index: 10000;
    //não adicione padding aqui e só estilize as classes especificas se possível
    margin: 0;

    .tooltip-inner {
        padding: 8px;
        max-width: 516px;
        min-width: 1%;
        border-radius: 6px;
        overflow: hidden;
        background: #362f2b;
        padding: 7px 8px;
        text-align: left;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        color: #fff;
    }

    .arrow {
        &::before {
            border-top-color: #362f2b;
            border-bottom-color: #362f2b;
        }
    }
}

@media (max-width: 768px) and (min-width: 480px) {
.tooltip-appointment-header {
        .tooltip-inner {
            max-width: 425px;
        }
    }
}

@media (max-width: 480px) {
.tooltip-appointment-header {
        .tooltip-inner {
            max-width: 288px;
        }
    }
}
</style>

<style lang="scss" scoped>
.production-progress__header {
    width: 100%;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 49px;
    z-index: 12;
    box-shadow: 0px 8px 30px 0px rgba(58, 46, 38, 0.1);

    .text-ellipsis {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
    }

    &.remove-fixed {
        top: initial !important;
        position: initial !important;
    }

    .production-progress__back-labels-container {
        display: flex;
        position: relative;

        .single-order-block {
            flex: 1;
            min-width: 0;
            margin: 0 16px;
        }

        .production-progress__back-button {
            min-width: max-content;
            padding: 15px 24px;
            border: 1px solid #cfc4be;
            border-left: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 16px;

            button {
                background: #fff;
                border-radius: 5px;
                border: none;
                padding: 0;
                outline: none;

                &:hover {
                    background: #ffede2;
                }

                &:active {
                    background: #ffdbc4;
                }

                svg {
                    height: 28px;
                    width: 28px;
                }
            }

            span {
                color: #4c4541;
                font-size: 16px;
                font-weight: 600;
                line-height: 26px;
            }

            .rnc-checklist-button {
                background: #974900;
                color: #fff;
                border: none;
                border-radius: 5px;
                padding: 8px 16px;
                font-size: 14px;
                font-weight: 600;
                line-height: 20px;
                cursor: pointer;
                white-space: nowrap;

                &:hover {
                    background: #7a3c00;
                }

                &:active {
                    background: #5c2d00;
                }
            }
        }

        .production-progress__labels-container {
            display: flex;
            justify-content: center;
            align-items: stretch;
            gap: 0;
            width: 100%;
            min-width: 0;
            padding: 0;
            overflow: hidden;
            border: 1px solid #cfc4be;
            border-left: 0;
            border-right: 0;

            &.--partial-button-header {
                padding: 0;
            }

            .single-order-block {
                flex: 1;
                min-width: 0;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 11px 24px;
            }

            .production-progress__single-order {
                color: #4c4541;
                font-size: 26px;
                font-weight: 600;
                line-height: 36px;
                text-align: center;
                width: 100%;
            }

            .header-stats-wrapper {
                margin-left: auto;
                display: flex;
                align-items: stretch;
                flex-shrink: 0;

                .header-total-time-block.--in-labels-container {
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    white-space: nowrap;
                    padding: 8px 24px;
                    border-left: 1px solid #cfc4be;
                    align-self: stretch;

                    &.--partial-button-header {
                        padding: 12px 24px;
                    }

                    .header-total-time-title {
                        color: #7e7570;
                        font-size: 12px;
                        font-weight: 600;
                        line-height: 16px;
                        text-align: center;
                    }

                    .header-total-time-value {
                        color: #4c4541;
                        font-size: 14px;
                        font-weight: 700;
                        line-height: 20px;
                        text-align: center;
                        letter-spacing: 0.5px;
                    }

                    @media (max-width: 480px) {
                        padding: 6px 12px;
                        .header-total-time-title {
                            font-size: 10px;
                            line-height: 14px;
                        }
                        .header-total-time-value {
                            font-size: 12px;
                            line-height: 16px;
                        }
                    }
                }

                .quantity-block {
                    white-space: nowrap;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    text-align: center;
                    padding: 8px 24px;
                    border-left: 1px solid #cfc4be;
                    align-self: stretch;

                    .quantity-title {
                        text-align: center;
                        color: #7e7570;
                        font-size: 12px;
                        font-weight: 600;
                        line-height: 16px;
                    }

                    .quantity-values-planned-produced {
                        color: #4c4541;
                        font-size: 14px;
                        font-weight: 600;
                        line-height: 20px;
                        text-align: center;
                    }

                    .produced {
                        color: #998f8a;
                        font-size: 14px;
                        font-weight: 600;
                        line-height: 20px;
                    }

                    &.--partial-button-header {
                        padding: 12px 24px;
                    }

                    @media (max-width: 480px) {
                        padding: 6px 12px;

                        .quantity-title {
                            font-size: 10px;
                            line-height: 14px;
                        }

                        .quantity-values-planned-produced {
                            font-size: 12px;
                            line-height: 16px;
                        }

                        .produced {
                            font-size: 12px;
                            line-height: 16px;
                        }
                    }
                }
            }
        }
    }

    .production-progress__status-container {
        padding: 7px 9px 6px;
        text-align: center;
        font-size: 16px;
        font-weight: 600;
        line-height: 26px;
        border-bottom: 1px solid #cfc4be;
    }

    &.point-resource-header {
        display: flex;
        justify-content: space-between;
        align-items: stretch;
        flex-direction: row;
        border-top: 1px solid #cfc4be;
        border-bottom: 1px solid #cfc4be;
        height: 60px;
        min-height: 60px;
        box-sizing: border-box;

        .production-progress__back-labels-container {
            width: 100%;
            border-right: 1px solid #cfc4be;
            display: flex;
            align-items: stretch;

            .production-progress__back-button {
                border: none;
                border-right: 1px solid #cfc4be;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 24px;
            }
        }

        .production-progress__labels-container {
            display: flex;
            justify-content: center;
            align-items: center;
            min-width: 0;
            width: 100%;
            flex: 1;
            color: #4c4541;
            text-align: center;
            font-size: 26px;
            font-weight: 600;
            line-height: 36px;
            border: none;
        }

        .header-total-time-block {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            align-self: stretch;
            flex-shrink: 0;
            padding: 0 20px;
            border-right: 1px solid #cfc4be;
            background-color: #fff;
            white-space: nowrap;

            .header-total-time-title {
                color: #2d2d2d;
                font-size: 11px;
                font-weight: 500;
                line-height: 14px;
                text-align: center;
            }

            .header-total-time-value {
                color: #000;
                font-size: 14px;
                font-weight: 700;
                line-height: 18px;
                text-align: center;
                letter-spacing: 0.5px;
            }

            @media (max-width: 768px) {
                padding: 0 10px;
                .header-total-time-title {
                    font-size: 10px;
                }
                .header-total-time-value {
                    font-size: 12px;
                }
            }
        }

        ::v-deep .extrusion-header-actions {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            padding: 0 16px;
            align-self: stretch;
            flex-shrink: 0;
            border-right: 1px solid #cfc4be;
            background-color: #fff;

            .btn-extrusion-header-action {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 38px;
                height: 38px;
                min-width: 38px;
                min-height: 38px;
                border-radius: 50%;
                background: transparent;
                border: none;
                cursor: pointer;
                padding: 0;
                margin: 0;
                outline: none;
                transition: all 0.2s ease-in-out;

                &:hover:not(:disabled) {
                    background-color: #ffede2;
                    transform: scale(1.08);
                }

                &:active:not(:disabled) {
                    background-color: #ffdbc4;
                    transform: scale(0.95);
                }

                &:disabled,
                &.btn-disabled {
                    opacity: 0.35;
                    cursor: not-allowed;
                    pointer-events: none;
                }

                .header-action-svg,
                svg {
                    width: 22px !important;
                    height: 22px !important;
                    display: block;
                    fill: #974900;
                    path {
                        fill: #974900;
                    }

                    &.icon-order-details {
                        width: 22px !important;
                        height: 23px !important;
                    }
                }

                &#btn-extrusion-order-details svg {
                    width: 22px !important;
                    height: 23px !important;
                }
            }
        }

        .production-progress__status-container {
            display: flex;
            white-space: nowrap;
            justify-content: center;
            align-items: center;
            align-self: stretch;
            flex-shrink: 0;
            padding: 0 24px;
            margin: 0;
            border: none;
            box-sizing: border-box;

            &.InProduction {
                background: #fff3e0;
                color: #ff9800;
            }

            &.Released {
                background: #e3f2fd;
                color: #2196f3;
            }

            &.Finished {
                background: #e8f5e9;
                color: #4caf50;
            }
        }
    }
}

@media (max-width: 1400px) {
    .production-progress__header {
        .production-progress__back-labels-container {
            .production-progress__back-button {
                padding: 10px 16px;

                span {
                    font-size: 14px;
                    line-height: 20px;
                }

                button svg {
                    height: 24px;
                    width: 24px;
                }
            }

            .production-progress__labels-container {
                padding: 0;

                .single-order-block {
                    padding: 6px 16px;
                }

                .production-progress__single-order {
                    font-size: 20px;
                    line-height: 28px;
                }

                .header-stats-wrapper {
                    .header-total-time-block.--in-labels-container,
                    .quantity-block {
                        padding: 6px 16px;
                    }

                    .quantity-block {
                        .quantity-title {
                            font-size: 10px;
                            line-height: 14px;
                        }
                        .quantity-values-planned-produced {
                            font-size: 12px;
                            line-height: 16px;
                        }
                        .produced {
                            font-size: 12px;
                            line-height: 16px;
                        }
                    }
                }
            }
        }

        .production-progress__status-container {
            padding: 4px 8px;
            font-size: 14px;
            line-height: 20px;
        }
    }
}

@media (max-width: 768px) and (min-width: 480px) {
    .production-progress__header {
        top: 86px;

        &.point-resource-header {
            top: 49px;

            .production-progress__back-button {
                button {
                    svg {
                        width: 28px !important;
                        height: 28px !important;
                    }
                }
            }
        }

        .production-progress__back-labels-container {
            .production-progress__back-button {
                padding: 6px 24px;

                button {
                    svg {
                        width: 46px;
                        height: 46px;
                    }
                }

                span {
                    font-size: 22px;
                    line-height: 30px;
                }

                .rnc-checklist-button {
                    font-size: 16px;
                    padding: 10px 20px;
                }
            }
        }
    }
}

@media (max-width: 768px) and (min-width: 714px) {
    .production-progress__header {
        .production-progress__back-labels-container {
            .production-progress__back-button {
                padding: 6px 24px;
            }

            .production-progress__labels-container {
                padding: 11px 29px;
            }
        }

        &.point-resource-header {
            display: flex;
            justify-content: space-between;
            flex-direction: row;
            border-top: 1px solid #cfc4be;
            border-bottom: 1px solid #cfc4be;

            .production-progress__back-labels-container {
                width: 100%;
                border-right: 1px solid #cfc4be;

                .production-progress__back-button {
                    border-right: none;
                }
            }

            .production-progress__labels-container {
                display: flex;
                justify-content: center;
                align-items: center;
                min-width: 0;
                width: 100%;
                flex: 1;
                color: #4c4541;
                text-align: center;
                font-size: 26px;
                font-weight: 600;
                line-height: 36px;
                /* 138.462% */
            }

            .production-progress__status-container {
                display: flex;
                white-space: nowrap;
                justify-content: center;
                align-items: center;
                align-self: stretch;
                flex-shrink: 0;
                padding: 0 20px;
                border: none;

                &.InProduction {
                    background: #fff3e0;
                    color: #ff9800;
                }

                &.Released {
                    background: #e3f2fd;
                    color: #2196f3;
                }

                &.Finished {
                    background: #e8f5e9;
                    color: #4caf50;
                }
            }
        }
    }
}

@media (max-width: 714px) {
    .production-progress__header {
        .production-progress__back-labels-container {
            flex-direction: column;

            .production-progress__back-button {
                width: 100%;
                justify-content: start;
                border-right: 0;
            }

            .production-progress__labels-container {
                padding: 12px 29px;
                border: 0;
            }
        }

        .production-progress__status-container {
            padding: 6px 9px;
            border: 1px solid #cfc4be;
            border-left: 0;
            border-right: 0;
        }

        &.point-resource-header {
            display: flex;
            justify-content: space-between;
            flex-direction: row;
            border-top: 1px solid #cfc4be;
            border-bottom: 1px solid #cfc4be;
            top: 48px;

            .production-progress__back-labels-container {
                width: 100%;
                border-right: 1px solid #cfc4be;
                flex-direction: row;

                .production-progress__back-button {
                    width: unset;
                    border-right: none;
                }
            }

            .production-progress__labels-container {
                display: flex;
                justify-content: center;
                align-items: center;
                min-width: 0;
                width: 100%;
                flex: 1;
                color: #4c4541;
                text-align: center;
                font-size: 26px;
                font-weight: 600;
                line-height: 36px;
                border: none;
            }

            .production-progress__status-container {
                display: flex;
                white-space: nowrap;
                justify-content: center;
                align-items: center;
                align-self: stretch;
                flex-shrink: 0;
                padding: 0 20px;
                border: none;

                &.InProduction {
                    background: #fff3e0;
                    color: #ff9800;
                }

                &.Released {
                    background: #e3f2fd;
                    color: #2196f3;
                }

                &.Finished {
                    background: #e8f5e9;
                    color: #4caf50;
                }
            }
        }
    }
}

@media (min-width: 481px) and (max-width: 1024px) {
    .production-progress__header:not(.point-resource-header) {
        .production-progress__back-labels-container {
            flex-direction: row;

            .production-progress__back-button {
                width: auto;
                flex: 0 0 auto;
                gap: 8px;
                padding: 6px 12px;
                border-right: 1px solid #cfc4be;

                button svg {
                    width: 24px;
                    height: 24px;
                }

                span {
                    font-size: 16px;
                    line-height: 22px;
                }
            }

            .production-progress__labels-container {
                min-width: 0;
                padding: 4px 12px;
                border-top: 1px solid #cfc4be;
                border-bottom: 1px solid #cfc4be;

                &.--partial-button-header {
                    padding: 0 12px;
                }

                .quantity-block.--partial-button-header {
                    padding: 6px 0 6px 12px;
                }
            }
        }

        .production-progress__status-container {
            padding: 3px 8px;
            line-height: 20px;
        }
    }
}

@media (max-width: 550px) {
    .production-progress__header {
        &.point-resource-header {
            flex-direction: column;

            .production-progress__back-labels-container {
                border-right: 0;
            }

            .production-progress__labels-container {
                font-size: 18px;
                line-height: 26px;
                /* 144.444% */
            }
        }
    }
}

@media (max-width: 480px) {
    .production-progress__header {
        top: 85px;

        .production-progress__back-labels-container {
            .production-progress__back-button {
                padding: 11px 16px;

                span {
                    font-size: 14px;
                    line-height: 20px;
                }

                .rnc-checklist-button {
                    font-size: 12px;
                    padding: 6px 12px;
                }
            }
        }

        .production-progress__labels-container {
            padding: 16px 29px !important;

            &.--partial-button-header {
                padding: 0 29px !important;
            }

            .production-progress__single-order {
                font-size: 18px !important;
                line-height: 26px !important;
            }
        }

        .production-progress__status-container {
            padding: 0 9px;
        }
    }
}
</style>
